import { useQueries, useQuery } from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from '../envelope';
import {
  iamKeys,
  type IamBinding,
  type IamBindingList,
  type IamMember,
  type IamOrgRef,
  type IamProject,
  type IamRole,
  type IamTeam,
} from './types';

const foundationApi = getHttpClient('foundationApi');

// ============================================
// Queries
// ============================================

/** The caller's IAM organizations; used to map a customer id to the org UUID. */
export function useIamOrg(org: string | undefined) {
  return useQuery({
    queryKey: iamKeys.me,
    enabled: !!org,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<IamOrgRef[]> => {
      const res = await foundationApi.get<Envelope<{ orgs?: IamOrgRef[] }>>('/iam/me');
      return res.data?.data?.orgs ?? [];
    },
    select: (orgs) => orgs.find((o) => o.customer_id === org || o.id === org),
  });
}

/** Roles that can be handed out in this organization (built-in plus the org's own). */
export function useIamRoles(org: string | undefined) {
  const { data: iamOrg } = useIamOrg(org);
  const orgId = iamOrg?.id;
  return useQuery({
    queryKey: iamKeys.catalog(orgId ?? ''),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<IamRole[]> => {
      const res = await foundationApi.get<Envelope<{ roles?: IamRole[] }>>('/iam/catalog', {
        params: { org_id: orgId },
      });
      return res.data?.data?.roles ?? [];
    },
  });
}

function iamPermissionsQuery(org: string | undefined, projectId?: string) {
  return {
    queryKey: iamKeys.permissions(org ?? '', projectId),
    enabled: !!org,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<string[]> => {
      const res = await foundationApi.get<Envelope<{ permissions?: string[] }>>(
        '/iam/me/permissions',
        { params: projectId ? { project_id: projectId } : undefined },
      );
      return res.data?.data?.permissions ?? [];
    },
  };
}

/**
 * The permission names the caller holds on an organization, or on one project
 * when `projectId` is given. Controls are gated on this, never on the legacy
 * project role, because it is the same decision the backend applies.
 */
export function useIamPermissions(org: string | undefined, projectId?: string) {
  return useQuery(iamPermissionsQuery(org, projectId));
}

/** True when the caller holds `permission` (false while loading or on failure). */
export function useIamCan(permission: string, org: string | undefined, projectId?: string) {
  return useIamCanState(permission, org, projectId).allowed;
}

/**
 * `useIamCan` plus whether the answer is in: a control that is off until `settled` says "checking", not "not allowed".
 * A failed answer settles as not allowed.
 */
export function useIamCanState(permission: string, org: string | undefined, projectId?: string) {
  const { data, isPending, fetchStatus } = useIamPermissions(org, projectId);
  return { allowed: !!data?.includes(permission), settled: !!org && !(isPending && fetchStatus !== 'idle') };
}

/**
 * The projects among `projectIds` on which the caller holds `permission`, one cached `/iam/me/permissions` answer per
 * project (the entries `useIamCan(permission, org, projectId)` reads). `settled` once every answer is in.
 */
export function useIamHeldOnProjects(permission: string, org: string | undefined, projectIds: readonly string[]) {
  return useQueries({
    queries: projectIds.map((id) => iamPermissionsQuery(org, id)),
    combine: (results) => ({
      held: projectIds.filter((_, i) => !!results[i]?.data?.includes(permission)),
      settled: !!org && results.every((r) => !(r.isPending && r.fetchStatus !== 'idle')),
    }),
  });
}

/** GET /iam/projects, the one query behind `useIamProjects`. */
function iamProjectsQuery(includeArchived = false) {
  return {
    queryKey: iamKeys.projects(includeArchived),
    queryFn: async (): Promise<IamProject[]> => {
      const res = await foundationApi.get<Envelope<{ projects?: IamProject[] }>>(
        `/iam/projects`,
        { params: includeArchived ? { include_archived: true } : undefined },
      );
      return res.data?.data?.projects ?? [];
    },
  };
}

/** GET /iam/teams, the one query behind `useIamTeams`. */
function iamTeamsQuery() {
  return {
    queryKey: iamKeys.teamList,
    queryFn: async (): Promise<IamTeam[]> => {
      const res = await foundationApi.get<Envelope<{ teams?: IamTeam[] }>>(`/iam/teams`);
      return res.data?.data?.teams ?? [];
    },
  };
}

/**
 * The projects the caller can reach. The request carries no org, so `org` only gates it; a
 * caller that has none (a picker in the shell) passes `{ enabled }` to run the query anyway.
 */
export function useIamProjects(org: string | undefined, includeArchived = false, options?: { enabled?: boolean }) {
  return useQuery({ ...iamProjectsQuery(includeArchived), enabled: options?.enabled ?? !!org });
}

/** The teams the caller owns or belongs to; `options.enabled` as in `useIamProjects`. */
export function useIamTeams(org: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({ ...iamTeamsQuery(), enabled: options?.enabled ?? !!org });
}

export function useIamTeamMembers(org: string | undefined, teamId: string | undefined) {
  return useQuery({
    queryKey: iamKeys.teamMembers(org ?? '', teamId ?? ''),
    enabled: !!org && !!teamId,
    queryFn: async (): Promise<IamMember[]> => {
      const res = await foundationApi.get<Envelope<{ members?: IamMember[] }>>(
        `/iam/teams/${teamId}/members`,
      );
      return res.data?.data?.members ?? [];
    },
  });
}

/** Organization-level bindings. */
export function useOrgBindings(org: string | undefined, enabled = true) {
  return useQuery({
    queryKey: iamKeys.orgBindings(org ?? ''),
    enabled: !!org && enabled,
    retry: false,
    queryFn: async (): Promise<IamBinding[]> => {
      const res = await foundationApi.get<Envelope<IamBindingList>>(`/iam/bindings`);
      return res.data?.data?.bindings ?? [];
    },
  });
}

/** One project's bindings, with the organization-level ones that also reach it. */
export function useProjectBindings(org: string | undefined, projectId: string | undefined) {
  return useQuery({
    queryKey: iamKeys.projectBindings(org ?? '', projectId ?? ''),
    enabled: !!org && !!projectId,
    retry: false,
    queryFn: async (): Promise<IamBindingList> => {
      const res = await foundationApi.get<Envelope<IamBindingList>>(
        `/iam/projects/${projectId}/bindings`,
        { params: { inherited: true } },
      );
      return { bindings: res.data?.data?.bindings ?? [], inherited: res.data?.data?.inherited ?? [] };
    },
  });
}
